import ExcelJS from 'exceljs'

import type {
  SupplierBidDetail,
  SupplierBidFeeItem,
  SupplierBidTransportMode
} from '@/api/bidding/supplier-bid'
import {
  getBiddingFeeDisplayName,
  sortBiddingFeeItems
} from '@/utils/bidding-fee-display'
import {
  biddingFeeBoundsGroups,
  biddingFeeBoundsKey,
  biddingPriceFees,
  getBiddingFeeBounds,
  getBiddingFeeQuoteValue,
  biddingPriceFeeColumnKey,
  biddingPriceFeeUnit,
  type BiddingPriceFee
} from '@/utils/bidding-fee-bounds'

interface BidFeeExportColumn {
  fee: BiddingPriceFee
  key: string
  bound?: 'min' | 'max'
}

/** 标书导出复用外链列顺序，主费恢复最低/M，其他费用按设置展示上下限。 */
function feeExportColumns(feeItems: SupplierBidFeeItem[]): BidFeeExportColumn[] {
  const priceFees = biddingPriceFees(feeItems)
  const enabled = biddingFeeBoundsGroups(feeItems)
  const lastIndexes = new Map(priceFees.map((fee, index) => [biddingFeeBoundsKey(fee), index]))
  return priceFees.flatMap((fee, index) => {
    const key = biddingFeeBoundsKey(fee)
    const columns: BidFeeExportColumn[] = [{ fee, key: `fee_${biddingPriceFeeColumnKey(fee)}` }]
    if (enabled.has(key) && lastIndexes.get(key) === index) {
      columns.push({ fee, key: `bound_${key}_min`, bound: 'min' },
        { fee, key: `bound_${key}_max`, bound: 'max' })
    }
    return columns
  })
}

interface ExportBidBooksOptions {
  details: SupplierBidDetail[]
  locale: string
  tenderCode: string
}

const modeOrder: SupplierBidTransportMode[] = ['FCL', 'LCL', 'AIR']

/**
 * 将开标后可见的结构化标书导出为按运输类型拆分的 Excel。
 *
 * @param options 标书详情、页面语言及招标编号
 * @returns 可直接下载的 Excel 文件 Blob
 */
export async function exportBidBooks(options: ExportBidBooksOptions): Promise<Blob> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'CargoWareX'
  workbook.title = options.tenderCode
  workbook.created = new Date()

  for (const mode of modeOrder) {
    const rows = buildModeRows(options.details, mode, options.locale)
    if (!rows.values.length) continue
    const worksheet = workbook.addWorksheet(mode)
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]
    worksheet.columns = buildColumns(rows.feeItems, options.locale)
    rows.values.forEach(row => worksheet.addRow(row))
    styleWorksheet(worksheet)
  }

  if (!workbook.worksheets.length) {
    workbook.addWorksheet('Bids').addRow([
      options.locale.toLowerCase().startsWith('en') ? 'No submitted bid data' : '暂无已提交应标数据'
    ])
  }
  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

function buildModeRows(details: SupplierBidDetail[], mode: SupplierBidTransportMode, locale: string) {
  const feeItems = uniqueFeeItems(details, mode)
  const values: Array<Record<string, string | number>> = []
  for (const detail of details) {
    const routes = new Map(detail.routes.map(route => [String(route.id), route]))
    const carriers = new Map(detail.carriers.map(carrier => [String(carrier.id), carrier]))
    for (const quote of detail.quotes) {
      const route = routes.get(String(quote.routeId))
      if (!quote.participating || !route || route.transportMode !== mode) continue
      const container = route.containers.find(item => String(item.id) === String(quote.containerId || ''))
      const carrierNames = (quote.transitCarrierIds || []).map(id => carriers.get(String(id)))
        .map(carrier => locale.toLowerCase().startsWith('en')
          ? (carrier?.nameEn || carrier?.nameCn || '')
          : (carrier?.nameCn || carrier?.nameEn || ''))
        .filter(Boolean)
      const row: Record<string, string | number> = {
        bidCode: detail.bidCode || '-',
        supplier: detail.supplierName || '-',
        contact: detail.contactName || '-',
        email: detail.contactEmail || '-',
        round: detail.currentRound,
        // 导出线路代码只使用报价行服务端生成值，不回退为招标航线代码。
        routeCode: quote.routeCode || '-',
        container: container?.containerType || '-',
        effectiveDate: quote.effectiveDate || '-',
        expiryDate: quote.expiryDate || '-',
        carrier: carrierNames.join('/') || '-',
        schedule: quote.vesselSchedule || quote.flightSchedule || '-',
        transitDays: quote.transitTimeDays ?? '',
        freeType: quote.freeContainerType || '-',
        mixedDays: quote.mixedDays ?? '',
        detentionDays: quote.separateContainerDays ?? '',
        demurrageDays: quote.separateStorageDays ?? '',
        storageDays: quote.storageDays ?? '',
        remark: quote.bidRemark || '-'
      }
      // 费用列按发布快照 ID 对齐，避免同名费用被错误合并。
      feeExportColumns(feeItems).forEach(column => {
        row[column.key] = column.bound
          ? getBiddingFeeBounds(quote, column.fee, feeItems)[column.bound] ?? ''
          : getBiddingFeeQuoteValue(quote, column.fee) ?? ''
      })
      values.push(row)
    }
  }
  return { feeItems, values }
}

function uniqueFeeItems(details: SupplierBidDetail[], mode: SupplierBidTransportMode): SupplierBidFeeItem[] {
  const items = new Map<string, SupplierBidFeeItem>()
  details.forEach(detail => detail.feeItems
    .filter(item => item.transportMode === mode)
    .forEach(item => items.set(String(item.id), item)))
  return sortBiddingFeeItems([...items.values()])
}

function buildColumns(feeItems: SupplierBidFeeItem[], locale: string): Partial<ExcelJS.Column>[] {
  const en = locale.toLowerCase().startsWith('en')
  const columns: Partial<ExcelJS.Column>[] = [
    { header: en ? 'Bid No.' : '应标编号', key: 'bidCode', width: 28 },
    { header: en ? 'Bidder' : '应标人', key: 'supplier', width: 24 },
    { header: en ? 'Contact' : '联系人', key: 'contact', width: 16 },
    { header: en ? 'Email' : '邮箱', key: 'email', width: 28 },
    { header: en ? 'Round' : '轮次', key: 'round', width: 10 },
    { header: en ? 'Route Code' : '线路代码', key: 'routeCode', width: 34 },
    { header: en ? 'Container' : '箱型', key: 'container', width: 12 },
    { header: en ? 'Effective Date' : '生效日期', key: 'effectiveDate', width: 15 },
    { header: en ? 'Expiry Date' : '截止日期', key: 'expiryDate', width: 15 },
    { header: en ? 'Carrier / Airline' : '船公司/航空公司', key: 'carrier', width: 22 },
    { header: en ? 'Schedule' : '班轮/航班星期', key: 'schedule', width: 18 },
    { header: en ? 'Transit Days' : '运输周期(天)', key: 'transitDays', width: 16 },
    { header: 'POD D&D Type', key: 'freeType', width: 16 },
    { header: 'POD D&D Mixed', key: 'mixedDays', width: 16 },
    { header: 'POD D&D Detention', key: 'detentionDays', width: 20 },
    { header: 'POD D&D Demurrage', key: 'demurrageDays', width: 20 },
    { header: 'POD Storage (Days)', key: 'storageDays', width: 20 }
  ]
  feeExportColumns(feeItems).forEach(column => {
    const name = getBiddingFeeDisplayName(column.bound
      ? { ...column.fee, quoteVariant: 'BASE' } : column.fee, locale)
    const unit = column.fee.quoteVariant === 'AIR_M' ? biddingPriceFeeUnit(column.fee, locale) : ''
    columns.push({
      header: `${name}${column.bound ? ` · ${column.bound === 'min' ? 'Min' : 'Max'}` : ''} ${column.fee.currency || ''}${unit ? `/${unit}` : ''}`.trim(),
      key: column.key,
      width: 20
    })
  })
  columns.push({ header: en ? 'Bid Remark' : '应标备注', key: 'remark', width: 28 })
  return columns
}

function styleWorksheet(worksheet: ExcelJS.Worksheet): void {
  const header = worksheet.getRow(1)
  header.font = { bold: true, color: { argb: 'FF1D2129' } }
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F3F5' } }
  header.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  worksheet.eachRow(row => {
    row.alignment = { vertical: 'middle', wrapText: true }
    row.eachCell(cell => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E6EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E6EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E6EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E6EB' } }
      }
    })
  })
  worksheet.autoFilter = { from: 'A1', to: `${worksheet.getColumn(worksheet.columnCount).letter}1` }
}
